<template>
  <v-container fluid>
    <div class="mx-4">
      <v-row>
        <v-col>
          <span class="base-title">{{ $t("rule.title") }}</span>
        </v-col>
      </v-row>

      <v-row class="pb-0">
        <v-col class="pb-0 px-4" cols="12">
          <v-row>
            <v-col v-bind="colAttrs">
              <v-text-field
                v-model.trim="ruleFilter.name"
                :label="$t('rule.fields.name.label')"
                clearable
                solo
                hide-details
              />
            </v-col>
            <v-col v-bind="colAttrs">
              <v-autocomplete
                :items="ruleTags"
                :return-object="false"
                v-model="ruleFilter.tags"
                deletable-chips
                :label="$t('rule.fields.tag.label')"
                multiple
                small-chips
                solo
                clearable
                hide-details
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip v-if="index < 1">
                    <span>{{ item }}</span>
                  </v-chip>
                  <span v-if="index === 1" class="foreground--text text-caption">
                    {{ formatter.getPluralText("outra", ruleFilter.tags) }}
                  </span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col v-bind="colAttrs">
              <v-autocomplete
                :items="movementTypes"
                :return-object="false"
                v-model="ruleFilter.movementTypes"
                deletable-chips
                :label="$t('rule.fields.movementType.label')"
                multiple
                small-chips
                item-value="code"
                item-text="name"
                solo
                clearable
                hide-details
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip v-if="index < 1">
                    <span>{{ item.name }}</span>
                  </v-chip>
                  <span v-if="index === 1" class="foreground--text text-caption">
                    {{ formatter.getPluralText(
                        "outra",
                        ruleFilter.movementTypes
                      )
                    }}
                  </span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col v-bind="colAttrs">
              <v-autocomplete
                :items="domains"
                v-model="ruleFilter.domain"
                :label="$t('rule.fields.domain.label')"
                item-text="label"
                item-value="value"
                solo
                clearable
                hide-details
              />
            </v-col>
            <v-col class="d-flex justify-end">
              <v-btn
                style="height: 49px; width: 300px;"
                large
                color="primary"
                @click="onClickSearch()"
              >
                <v-icon style="font-size: 17px" class="mr-2" color="foreground-accent">fa-search</v-icon>
                {{ $t('common.search') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col class="pb-0 px-4 my-5 d-flex justify-end" cols="12">
          <v-btn
            style="height: 49px; width: 300px;"
            large
            color="primary"
            @click="createRule()"
          >
            <v-icon style="font-size: 17px" class="mr-2" color="foreground-accent">fa-plus</v-icon>
            {{ $t('rule.create') }}
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="px-4">
          <v-data-table
          :headers="headers"
          :items="rules"
          :loading="loadingRules"
          item-key="id"
          class="default-table-height elevation-1"
          :page="page"
          :items-per-page="itemsPerPage"
          hide-default-header
          fixed-header
          :footer-props="{
            itemsPerPageOptions: [25, 50, 75, 100],
            'items-per-page-text': $t('rule.table.itemsPerPageText')
          }"
        >
          <template v-slot:[`header`]="{ props }">
            <thead>
              <tr>
                <th v-for="(header, index) in props.headers" :key="index" :style="{ 'background-color': 'var(--v-muted-base) !important', 'text-align': 'start' }">
                  <label class="label base-color">
                    {{ header.text }}
                  </label>
                </th>
              </tr>
            </thead>
          </template>
          <template v-slot:[`item.criticism`]="{ item }">
            <span>
              {{
                item.criticismId
                  ? getCriticismById(item.criticismId)
                  : '-'
              }}
            </span>
          </template>
          <template v-slot:[`item.domain`]="{ item }">
            <v-chip small :color="item.domain === 'STATEMENT' ? 'info' : ''">
              {{ formatDomain(item.domain) }}
            </v-chip>
          </template>
          <template v-slot:[`item.movementTypes`]="{ item }">
            <span>{{ movementTypeFormater(item.movementTypes) }}</span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-menu transition="slide-y-transition" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn color="primary" icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <TodoListActions :actions="getActionsRulesForItem(item)" />
            </v-menu>
          </template>
        </v-data-table>
        </v-col>
      </v-row>
    </div>

    <v-row>
      <v-col class="pr-6" cols="12" align="end">
        <v-footer>
          <v-row class="d-flex pt-6 justify-end">
            <v-btn
              x-large
              outlined
              color="primary"
              width="250px"
              @click="onClickCancel()"
            >
            {{ $t('common.back') }}
            </v-btn>
          </v-row>
        </v-footer>
      </v-col>
    </v-row>

    <ExclusionDynamicModal :show="showExclusionDynamicModal" :loadingConfirm="loadingConfirm" @close="closeExclusionDynamicModal" @confirm="confirmExclusionDynamicModal">
      <template slot="header"> {{ $t('rule.delete') }} </template>
      <template slot="content">
        {{ $t('rule.messages.confirmDelete') }}
        <v-alert color="muted" class="mt-3">
          <span class="title">{{ ruleToBeDeleted.name }}</span>
        </v-alert>
      </template>
    </ExclusionDynamicModal>

    <SnackbarCustomize ref="SnackbarCustomize" />
  </v-container>
</template>

<script>
import Formatters from '@/shared/formatters/formatters';
import TagService from '@/services-http/sdi/TagService';
import MetaRuleService from '@/services-http/sdi/MetaRuleService';
import TodoListActions from '@/components/TodoList/TodoListActions.vue';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import VerifyRoutesMixin from '@/shared/mixins/routeManagement/verifyRoutesMixin';
import MovementRecordsMixin from '@/shared/mixins/movementRecords/movementRecordsMixin';
import ExclusionDynamicModal from '@/components/ExclusionDynamicModal/ExclusionDynamicModal.vue';
import CriticismService from '@/services/criticism-service';
import { defaultDomains } from '@/store/modules/rule/rule-config';

export default {
  name: 'GeneralRules',
  components: { TodoListActions, SnackbarCustomize, ExclusionDynamicModal },
  data: () => ({
    loadingRules: false,
    isRHProtegido: false,
    showExclusionDynamicModal: false,
    loadingConfirm: false,

    page: 1,
    totalPages: 0,
    itemsPerPage: 25,

    options: {},
    ruleToBeDeleted: {},
    ruleFilter: {
      tags: [],
      name: null,
      movementTypes: [],
      domain: null,
    },

    movementTypes: [],
    domains: [],
    critics: [],

    ruleTags: [],
    rules: [],
    colAttrs: {
      cols: 12,
      xl: 3,
      lg: 3,
      md: 4,
      sm: 6,
    },
    criticismService: null,
  }),

  mixins: [
    VerifyRoutesMixin,
    MovementRecordsMixin,
  ],

  created() {
    this.options.mounted = true;
    this.formatter = new Formatters();
    this.tagService = new TagService();
    this.metaRuleService = new MetaRuleService();
    this.criticismService = new CriticismService();
  },

  async mounted() {
    await Promise.all([
      this.loadTags(),
      this.loadMetaRules(),
      this.loadMovementTypes(),
      this.loadCriticism(),
      this.loadDomains(),
    ]);
  },

  methods: {
    loadTags() {
      this.tagService.FindAll().then((response) => {
        if (response && response.data) {
          this.ruleTags = response.data;
        }
      }).catch((error) => {
        this.$refs.SnackbarCustomize.open('error', this.$t('rule.fields.tag.messages.errors.search'));
      });
    },
    async loadCriticism() {
      await CriticismService.GetCriticism().then((result) => {
        this.critics = [];

        if (Array.isArray(result)) {
          this.critics = result.sort((a, b) => (a.message > b.message ? 1 : -1));
        }
      }).catch((error) => {
        this.$refs.SnackbarCustomize.open('error', this.$t('rule.fields.criticism.messages.errors.search'));
      });
    },
    async loadMetaRules() {
      try {
        this.loadingRules = true;
        const rules = await this.loadMetaRulesByFilters();
        this.rules = rules;
      } catch (error) {
        this.$refs.SnackbarCustomize.open('error', this.$t('rule.fields.name.messages.errors.search'));
      } finally {
        this.loadingRules = false;
      }
    },
    async loadMetaRulesByFilters() {
      const params = new URLSearchParams();

      if (Array.isArray(this.ruleFilter.tags) && this.ruleFilter.tags.length > 0) {
        params.append('tags', this.ruleFilter.tags);
      }

      if (Array.isArray(this.ruleFilter.movementTypes) && this.ruleFilter.movementTypes.length > 0) {
        params.append('movementTypes', this.ruleFilter.movementTypes);
      }

      if (this.ruleFilter.name) {
        params.append('name', this.ruleFilter.name);
      }

      if (this.ruleFilter.domain) {
        params.append('domain', this.ruleFilter.domain);
      }

      let response = null;

      if (params.size > 0) {
        response = await this.metaRuleService.FindByParams(params);
      } else {
        response = await this.loadAllMetaRules();
      }

      return response.data;
    },
    async loadAllMetaRules() {
      const response = await this.metaRuleService.FindAll();
      return response;
    },
    onClickSearch() {
      this.loadMetaRules();
    },
    createRule() {
      this.redirectRouter('RuleNew');
    },
    movementTypeFormater(mt) {
      let text = '-';
      mt.forEach((mts, index) => {
        if (index === 0) {
          text = this.formatter.formatToTitleCase(mts.name);
        } else {
          text += `, ${this.formatter.formatToTitleCase(mts.name)}`;
        }
      });

      return text;
    },
    formatDomain(domainValue) {
      if (!domainValue) {
        return this.$t('rule.domainOptions.movement');
      }
      const found = this.domains.find((d) => d.value === domainValue);
      return found ? found.label : domainValue;
    },
    loadDomains() {
      this.domains = defaultDomains;
    },
    getCriticismById(criticismId) {
      const criticism = this.critics.find((critic) => critic.id === criticismId);

      return criticism.message;
    },
    getActionsRulesForItem(item) {
      const actions = [
        {
          label: this.$t('common.edit'),
          icon: 'fa fa-edit',
          color: 'primary',
          condition: item,
          handler: () => this.editRule(item),
        },
        {
          label: this.$t('common.copy'),
          icon: 'fa fa-copy',
          color: 'primary',
          condition: item,
          handler: () => this.copyRule(item),
        },
        {
          label: this.$t('common.delete'),
          icon: 'fas fa-trash',
          color: 'primary',
          condition: item,
          handler: () => this.openExclusionDynamicModal(item),
        },
      ];

      const sortedActions = this.formatter.sortByProperty(actions, 'label');

      return sortedActions;
    },
    copyRule(rule) {
      this.redirectRouter('CopyRule', { isRHProtegido: this.isRHProtegido }, { copyId: rule.idLastVersion });
    },
    editRule(rule) {
      this.redirectRouter('RuleEdit', { isEdit: true, isRHProtegido: this.isRHProtegido }, { id: rule.idLastVersion });
    },
    openExclusionDynamicModal(rule) {
      this.ruleToBeDeleted = {};
      this.ruleToBeDeleted = rule;
      this.showExclusionDynamicModal = true;
    },
    closeExclusionDynamicModal() {
      this.showExclusionDynamicModal = false;
    },
    confirmExclusionDynamicModal() {
      this.loadingConfirm = true;
      this.metaRuleService.Delete(this.ruleToBeDeleted.id).then(() => {
        this.$refs.SnackbarCustomize.open('success', this.$t('rule.messages.ruleDeleted'));
        this.loadMetaRules();
      }).catch(() => {
        this.$refs.SnackbarCustomize.open('error', this.$t('rule.messages.ruleDeleteError'));
      }).finally(() => {
        this.loadingConfirm = false;
        this.closeExclusionDynamicModal();
      });
    },
    onClickCancel() {
      this.$router.push({ name: 'HomeAllMenus' });
    },
  },

  computed: {
    headers() {
      return [
        {
          text: this.$t('rule.fields.name.title'),
          align: 'start',
          value: 'name',
          sortable: false,
        },
        {
          text: this.$t('rule.fields.criticism.title'),
          value: 'criticism',
          align: 'start',
          sortable: false,
        },
        {
          text: this.$t('rule.fields.domain.title'),
          value: 'domain',
          align: 'start',
          sortable: false,
        },
        {
          text: this.$t('rule.fields.movementType.title'),
          value: 'movementTypes',
          align: 'start',
          sortable: false,
        },
        {
          text: this.$t('common.actions'),
          value: 'actions',
          align: 'start',
          sortable: false,
        },
      ];
    },
  },
};
</script>
